<script
    context="module"
    lang="ts"
>
    export function getAllTaskImageReferences(task: any): Set<string> {
        const imageReferences = new Set<string>()

        function addImageIds(obj: any, path: string = "") {
            if (Array.isArray(obj)) {
                obj.forEach((item, index) => addImageIds(item, `${path}[${index}]`))
            } else if (obj && typeof obj === "object") {
                for (const key in obj) {
                    const newPath = path ? `${path}.${key}` : key
                    if (key === "imageIds" || key === "images" || key.includes("mageIds")) {
                        const ids = Array.isArray(obj[key]) ? obj[key] : [obj[key]]
                        ids.forEach((id) => {
                            if (typeof id === "string" && id.trim()) {
                                imageReferences.add(id)
                            }
                        })
                    } else if (typeof obj[key] === "object") {
                        addImageIds(obj[key], newPath)
                    }
                }
            }
        }

        addImageIds(task)

        task.images?.forEach((image) => {
            imageReferences.add(image.id)
        })
        return imageReferences
    }
</script>

<script lang="ts">
    import { deleteDBEntry, getDBEntries, getDBEntry, putDBEntry } from "../../../../api"
    import Modal from "../../../components/Modal.svelte"
    import TaskImage from "./TaskImage.svelte"
    import MapPreview from "../MapPreview.svelte"
    import Cta from "../../../components/CTA.svelte"
    import { downloadAllImages, downloadSelectedImages } from "../helper"
    import hash from "object-hash"
    import MainModalLowerLine from "../MainModalLowerLine.svelte"
    import { isMobile, newNotification } from "../../../store"
    import { copyObject } from "../../utils"
    import { createEventDispatcher, onMount } from "svelte"
    import UploadImage from "./UploadImage.svelte"
    import ImagesUpload from "../../inputfields/ImageUpload/ImagesUpload.svelte"
    export let task: TaskOilSpillRemoval | TaskTowing,
        showModal = false,
        author: string = "",
        recoverImages: boolean = false

    const dispatch = createEventDispatcher()

    $: imageReferences = getAllTaskImageReferences(task)
    function isFileInTaskFiles(files: TaskFile[], fileId: string) {
        return files.some((file) => file.id === fileId)
    }
    $: {
        if (task && task.images && !showModal) {
            let hasChanges = false

            imageReferences.forEach((imageReference) => {
                if (!isFileInTaskFiles(task.images, imageReference)) {
                    const newFile = { id: imageReference, comment: "", inMissionProtocol: false }
                    task.images.push(newFile)
                    hasChanges = true // Mark that a change has been made
                }
            })

            if (hasChanges) {
                resetOriginalTaskHash()
                // @ts-ignore
                putDBEntry("task", {
                    id: task.id,
                    images: task.images,
                }).catch((error) => {
                    console.error("Error updating task", error)
                })
            }
        }
    }
    function upadateTask() {
        // @ts-ignore
        putDBEntry("task", {
            id: task.id,
            images: task.images,
        })
            .catch((error) => {
                console.error("Error updating task", error)
            })
            .finally(async () => {
                try {
                    // await Promise.all(imagesToDelete.map((id) => deleteDBEntry("media", id)))
                } catch {
                    // most likely the image was already deleted
                }

                // Prepare and execute get/put requests concurrently
                await Promise.all(
                    task.images.map(async (image) => {
                        const originalImage = originalObject.find((el) => el.id === image.id)
                        if (originalImage && originalImage.comment !== image.comment) {
                            const mediaEntry = await getDBEntry("media", { _id: image.id })
                            await putDBEntry("media", {
                                id: image.id,
                                ...mediaEntry,
                                comment: image.comment,
                            })
                        }
                    })
                )
                dispatch("updateTaskImages", task.images)
                setTimeout(() => {
                    originalTaskImagesHash = hash(task.images || [])
                    originalObject = copyObject(task.images)
                    newNotification({
                        html: "Einsatzbilder aktuallisiert",
                        class: "success",
                    })
                }, 100)
            })
    }
    let mediaImages: Media[] = [],
        loading = true
    async function restoreImages() {
        // get all images in media that have  attachedTask = task.id

        const filter: any = { attachedTask: task.id }
        const mediaImages = await getDBEntries("media", filter)
        // verify if all ids are in task.images, and if not, add them and if there is an id, that is not in media, update media entry (fetch it and update attachedEntry)
        task.images.forEach((image) => {
            if (!mediaImages.find((el) => el.id === image.id)) {
                // update media entry
                getDBEntry("media", { _id: image.id }).then((response) => {
                    putDBEntry("media", {
                        id: image.id,
                        ...response,
                        attachedTask: task.id,
                    })
                })
            }
        })
        mediaImages.forEach((mediaImage) => {
            if (!task.images.find((el) => el.id === mediaImage.id)) {
                task.images.push({ id: mediaImage.id, comment: "", inMissionProtocol: false })
            }
        })

        upadateTask()
    }
    async function setMediaImages(imageReferences: string[]) {
        const allImagesFetched = Array.from(imageReferences || []).every((ref) =>
            mediaImages.some((mi) => mi.id === ref)
        )
        if (allImagesFetched) {
            loading = false
            return
        }
        if (!imageReferences || imageReferences.length === 0) {
            mediaImages = []
            loading = false
            return
        }

        const ids = Array.from(imageReferences) as string[]
        const batches: string[][] = []
        for (let i = 0; i < ids.length; i += 50) {
            batches.push(ids.slice(i, i + 50))
        }

        loading = true

        const mediaImageTemp: Media[] = []

        for (const batchIds of batches) {
            const filter: any = { _id: { $in: batchIds } }
            if (author) filter["author.id"] = author

            try {
                const response = await getDBEntries("media", filter)

                mediaImageTemp.push(...response)
                for (const image of task.images) {
                    if (!image.comment) {
                        const match = response.find((el) => el.id === image.id)
                        if (match) image.comment = match.comment
                    }
                }
            } catch (err) {
                console.error("Fehler beim Laden eines Chunks:", err)
            }
        }

        // check if all images in mediaImageTemp are already in mediaImages, if so, return
        const allImagesIncluded = mediaImageTemp.every((img) => mediaImages.some((el) => el.id === img.id))
        if (!allImagesIncluded) {
            mediaImages = mediaImageTemp
        }
        loading = false
    }
    function resetOriginalTaskHash() {
        originalTaskImagesHash = hash(task.images)
        originalObject = copyObject(task.images)
    }

    let originalTaskImagesHash: string,
        originalObject: any = copyObject(task.images)
    $: if (!task?.images) {
        task.images = []
        resetOriginalTaskHash()
    }
    $: if (task?.images && !originalTaskImagesHash) {
        resetOriginalTaskHash()
    }
    $: setMediaImages(Array.from(imageReferences).filter((imgRef: string) => imagesToDelete.indexOf(imgRef) === -1))
    $: catchAccidentalExit = {
        active: true,
        originalHash: originalTaskImagesHash,
        currentObject: task.images,
        originalObject: originalObject,
    }
    $: {
        const newValues = mediaImages?.map((image) => image.id)
        if (JSON.stringify(newValues) !== JSON.stringify(task.images) && !loading) {
            // get all els that are not in the media images
            const newImages = newValues.filter((el) => !task.images.find((image) => image.id === el))
            const removedImages = task.images.filter((el) => !newValues.find((image) => image === el.id))
            if (removedImages) {
                removedImages.forEach((image) => {
                    const index = task.images.findIndex((el) => el.id === image.id)
                    task.images.splice(index, 1)
                })
            }
            if (newImages) {
                newImages.forEach((image) => {
                    task.images.push({ id: image, comment: "", inMissionProtocol: false })
                })
            }
        }
    }
    onMount(() => {
        setTimeout(resetOriginalTaskHash, 500)
        task.images.forEach((image) => {
            image.comment = ""
        })
    })
    let showImageUploadModal = false
    let selectedImages: boolean[] = []
    let imagesToDelete: string[] = []
</script>

<Modal
    show="{showModal}"
    mainModal="{true}"
    showCloseIcon="{true}"
    size="xl"
    contentId="taskImagesModal"
    on:close="{() => {
        task.images = copyObject(originalObject)
        originalTaskImagesHash = hash(task.images)
        showModal = false
    }}"
    catchAccidentalExit="{catchAccidentalExit}"
>
    <div
        slot="header"
        class="inner-modal-header"
    >
        <div class="upper">
            <div class="title">
                <img
                    src="../../../assets/icons/solar_camera-outline.svg"
                    alt="icon"
                />
                <p>Einsatzbilder</p>
            </div>
        </div>

        <MainModalLowerLine task="{task}" />
    </div>
    <div class="wrapper">
        <div class="content">
            <ul>
                <UploadImage
                    on:uploadImage="{() => {
                        showImageUploadModal = true
                    }}"
                />
                {#each task.images || [] as image, i (image.id)}
                    {#each mediaImages as media}
                        {#if media.id === image.id}
                            <TaskImage
                                bind:image="{image}"
                                on:remove="{() => {
                                    imagesToDelete.push(image.id)
                                    mediaImages = mediaImages.filter((el) => el.id !== image.id)
                                    task.images = task.images.filter((el) => el.id !== image.id)
                                }}"
                                bind:media="{media}"
                                bind:selected="{selectedImages[i]}"
                            />
                        {/if}
                    {/each}
                {/each}
            </ul>
        </div>
        {#if showModal}
            <MapPreview
                images="{mediaImages}"
                maxHeightElement="taskImagesModal"
            />
        {/if}
    </div>
    <div
        class="inner-footer"
        slot="footer"
    >
        <div>
            <Cta
                type="tertiaryButton"
                icon="solar_download-minimalistic-outline"
                content="{$isMobile ? '' : 'Auswahl herunterladen'}"
                disabled="{!selectedImages.some(Boolean)}"
                on:click="{(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    const selectedTaskFiles = task.images.filter((_, i) => selectedImages[i])
                    downloadSelectedImages(selectedTaskFiles, mediaImages)
                }}"
            />
        </div>
        <div>
            <Cta
                type="tertiaryButton"
                icon="solar_refresh-bold"
                content="{$isMobile ? '' : 'Alle Fotos Wiederherstellen'}"
                on:click="{(e) => {
                    restoreImages()
                }}"
            />
            <Cta
                type="tertiaryButton"
                icon="solar_download-minimalistic-outline"
                content="{$isMobile ? 'Alles' : 'Alle Fotos herunterladen'}"
                disabled="{task.images.length === 0}"
                on:click="{(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    downloadAllImages(task, mediaImages)
                }}"
            />
            <Cta
                type="primaryButton"
                icon="solar_diskette-outline"
                content="{$isMobile ? '' : 'Speichern'}"
                disabled="{originalTaskImagesHash === hash(task.images || [])}"
                on:click="{(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    upadateTask()
                }}"
            />
        </div>
    </div>
    {#if showImageUploadModal}
        <ImagesUpload
            bind:showImageUploadModal="{showImageUploadModal}"
            bind:imagesData="{mediaImages}"
            on:forceUpdate="{() => {
                if (originalTaskImagesHash !== hash(task.images || [])) upadateTask()
            }}"
            taskId="{task.id}"
        />
    {/if}
</Modal>

<style lang="less">
    @import "../../../../../assets/variables.less";
    :global .inner-modal-header {
        display: flex;
        flex-direction: column;
        gap: 1.2rem;
        flex-grow: 1;
        max-width: 100%;
        color: var(--white-100);
        .upper {
            display: flex;
            width: 100%;
            justify-content: space-between;
            .title {
                display: flex;
                gap: 0.6rem;
                align-items: center;
                p {
                    font-size: var(--headlines-h2-font-size);
                }
            }
            align-items: center;
        }
        .lower {
            display: flex;
            align-items: center;
            width: 100%;
            justify-content: space-between;
            .col1,
            .col2 {
                display: flex;
                flex-direction: column;
                gap: 6px;
                span {
                    font-family: Inter-Bold;
                    font-size: 0.5rem;
                    line-height: 0.5rem;
                    font-weight: 700;
                    text-transform: uppercase;
                }

                & > div {
                    display: flex;
                    gap: 0.6rem;
                    .ulcontent {
                        display: flex;
                        align-items: center;
                        li {
                            border-left: 1px solid var(--white-100);
                            padding: 0px 0.6rem;
                            height: fit-content;
                            font-size: var(--headlines-h2-font-size);
                            font-weight: 400;
                            font-family: Inter-Regular;
                            line-height: 1.6rem;
                            color: var(--white-100);
                            &:first-child {
                                border-left: none;
                                padding-left: 0px;
                            }
                        }
                    }
                }
            }
            @media @mobile {
                flex-direction: column;
                align-items: flex-start;
                gap: 1.2rem;
                padding-bottom: 1.2rem;
            }
        }
    }
    .wrapper {
        display: grid;
        gap: 1.2rem;
        grid-template-columns: 2fr 1fr;
        max-height: 60vh;
        height: 100%;
        overflow: hidden;
        .content {
            height: 95%;
            overflow: scroll;
            ul {
                display: grid;
                grid-template-columns: repeat(auto-fill, 12rem);
                gap: 6px;
                // make it centered
                justify-content: start;
            }
        }
        @media @mobile {
            display: flex;
            flex-direction: column-reverse;
            overflow-y: auto;
            .content {
                overflow: visible;
                height: unset;
                ul {
                    display: flex;
                    flex-direction: column;
                }
            }
        }
    }
    :global .inner-footer {
        display: flex;
        width: 100%;
        justify-content: space-between;
        background-color: var(--grey-300);
        padding: 0.6rem 0px;
        div {
            display: flex;
            gap: 0.6rem;
            @media @mobile {
                gap: 0.3rem;
            }
        }
    }
</style>
